<template>
  <div v-if="tablist.length" class="mtx-tabs">
    <div role="tablist" :aria-label="label" :aria-orientation="orientation" class="flex">
      <button
        v-for="(tab, index) in tablist"
        :key="tab"
        ref="tab"
        @keydown="onKeydown"
        @click="activeIndex = index"
        :aria-selected="index === activeIndex"
        :tabindex="index === activeIndex ? false : -1"
        :id="`${id}-tab-${index}`"
        :aria-controls="`${id}-panel-${index}`"
        role="tab"
        class="btn--link flex-grow"
      >
        <span class="mtx-tabs__tab-content px-2 py-1" tabindex="-1">{{ tab }}</span>
      </button>
    </div>

    <div
      v-for="(tab, index) in tablist"
      :key="tab"
      :id="`${id}-panel-${index}`"
      :aria-labelledby="`${id}-tab-${index}`"
      :hidden="index !== activeIndex"
      tabindex="0"
      role="tabpanel"
      class="px-2 py-2 bg-white"
    >
      <div class="mtx-tabs__panel-content" tabindex="-1">
        <slot :name="tab"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import keycodes from "@/data/keycodes"

// Add or substract depending on key pressed
const directions = {
  [keycodes.LEFT]: -1,
  [keycodes.UP]: -1,
  [keycodes.RIGHT]: 1,
  [keycodes.DOWN]: 1
}

export default {
  props: {
    label: String,

    orientation: {
      type: String,
      default: "horizontal"
    }
  },

  data: () => ({
    activeIndex: 0
  }),

  computed: {
    tablist() {
      return Object.keys(this.$slots)
    },

    id() {
      if (this.$attrs.id) return this.$attrs.id

      return Array(6)
        .fill()
        .map(() => Math.floor(36 * Math.random()).toString(36))
        .join("")
    }
  },

  methods: {
    onKeydown(event) {
      const { keyCode } = event

      switch (keyCode) {
        case keycodes.END:
          event.preventDefault()
          this.activeIndex = this.tablist.length - 1
          this.setFocus()
          break
        case keycodes.HOME:
          event.preventDefault()
          this.activeIndex = 0
          this.setFocus()
          break

        // Up and down are in keydown because we need to prevent page scroll >:)
        case keycodes.LEFT:
        case keycodes.RIGHT:
        case keycodes.UP:
        case keycodes.DOWN:
          this.determineOrientation(event)
          break
      }
    },

    // When a tablist's aria-orientation is set to vertical, only up and down arrow should function. In all other cases only left and right arrow function.
    determineOrientation(event) {
      const keyCode = event.keyCode
      let proceed = false

      if (this.orientation === "vertical") {
        if (keyCode === keycodes.UP || keyCode === keycodes.DOWN) {
          event.preventDefault()
          proceed = true
        }
      } else {
        if (keyCode === keycodes.LEFT || keyCode === keycodes.RIGHT) {
          proceed = true
        }
      }

      if (proceed) {
        this.switchTabOnArrowPress(event)
        this.setFocus()
      }
    },

    // Either focus the next, previous, first, or last tab depening on key pressed
    switchTabOnArrowPress(event) {
      const keyCode = event.keyCode
      /* istanbul ignore next */
      if (!directions[keyCode]) return

      const nextIndex = this.activeIndex + directions[keyCode]

      if (nextIndex < 0) {
        this.activeIndex = this.$refs.tab.length - 1
      } else if (nextIndex >= this.$refs.tab.length) {
        this.activeIndex = 0
      } else {
        this.activeIndex = nextIndex
      }
    },

    setFocus() {
      this.$refs.tab[this.activeIndex].focus()
    }
  }
}
</script>

<style lang="scss">
.mtx-tabs {
  [role="tablist"] {
    overflow: visible;
    margin: 0 0 -1px;
  }

  [role="tab"] {
    position: relative;
    border: 1px solid $stroke;
    border-bottom: 0;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;

    &[aria-selected="true"] {
      color: inherit;
      background: #fff;
    }
  }

  &__tab-content,
  &__panel-content {
    display: block;

    &:focus {
      outline: 0;
    }
  }

  [role="tabpanel"] {
    border: 1px solid $stroke;
  }
}
</style>
